export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// نفس القائمة المسموحة في الباك اند (api-server-v2 → sanitizeRichTextHtml).
const ALLOWED_STYLE_PROPS = new Set([
  'color', 'background-color', 'text-align', 'direction',
  'font-size', 'font-family', 'font-weight', 'font-style',
  'text-decoration', 'text-decoration-line', 'line-height',
  'margin-left', 'margin-right', 'margin-inline-start', 'margin-inline-end',
  'padding', 'padding-left', 'padding-right', 'padding-inline-start', 'padding-inline-end',
  'border', 'border-collapse', 'border-color', 'width', 'min-width', 'vertical-align',
]);

export const sanitizeInlineStyle = (css) =>
  String(css || '')
    .replace(/&quot;/gi, "'")
    .split(';')
    .map((declaration) => {
      const index = declaration.indexOf(':');
      if (index < 0) return '';
      const prop = declaration.slice(0, index).trim().toLowerCase();
      const val = declaration.slice(index + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop) || !val) return '';
      if (/url\s*\(|expression|javascript:|[<>"\\]/i.test(val)) return '';
      return `${prop}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');

export const sanitizeRichHtml = (html) => {
  let out = String(html || '');
  out = out.replace(/<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
  out = out.replace(/<\s*\/?\s*(iframe|object|embed|link|meta)[^>]*>/gi, '');
  out = out.replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '');
  out = out.replace(/\sstyle\s*=\s*(['"])(.*?)\1/gi, (_match, _quote, css) => {
    const safe = sanitizeInlineStyle(css);
    return safe ? ` style="${safe}"` : '';
  });
  out = out.replace(/\sclass\s*=\s*(['"]).*?\1/gi, '');
  out = out.replace(/\sid\s*=\s*(['"]).*?\1/gi, '');
  out = out.replace(/\shref\s*=\s*(['"])\s*javascript:[\s\S]*?\1/gi, ' href="#"');
  return out;
};

const applyInlineMarkdown = (text) =>
  String(text ?? '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

const toMarkdownHtml = (raw) => {
  const numbered = /^\s*(?:\d+|[٠-٩]+)\s*[.)-]\s*/;
  const inlineNumbered = /(\S)\s+((?:\d+|[٠-٩]+)\s*[.)-]\s*)/g;
  const lines = String(raw ?? '').replace(inlineNumbered, '$1\n$2').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].trim()) {
      i += 1;
      continue;
    }
    if (/^\s*-\s+/.test(lines[i])) {
      const items = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ''));
        i += 1;
      }
      blocks.push(`<ul>${items.map((it) => `<li>${applyInlineMarkdown(escapeHtml(it))}</li>`).join('')}</ul>`);
      continue;
    }
    if (numbered.test(lines[i])) {
      const items = [];
      while (i < lines.length && numbered.test(lines[i])) {
        items.push(lines[i].replace(numbered, ''));
        i += 1;
      }
      blocks.push(`<ol>${items.map((it) => `<li>${applyInlineMarkdown(escapeHtml(it))}</li>`).join('')}</ol>`);
      continue;
    }
    const paragraph = [];
    while (i < lines.length && lines[i].trim() && !/^\s*-\s+/.test(lines[i]) && !numbered.test(lines[i])) {
      paragraph.push(lines[i]);
      i += 1;
    }
    const p = applyInlineMarkdown(escapeHtml(paragraph.join('\n'))).replace(/\n/g, '<br/>');
    blocks.push(`<p>${p}</p>`);
  }
  return blocks.join('');
};

const getReadableHtmlText = (html) => {
  const value = String(html || '');
  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = value;
    return container.innerText || container.textContent || '';
  }
  return value
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(div|p|li|ol|ul)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, ' ');
};

const hasInlineNumbering = (value) =>
  /(?:^|\s)(?:\d+|[٠-٩]+)\s*[.)-]\s*\S/m.test(String(value || ''));

export const toSafeHtml = (value) => {
  const str = String(value ?? '');
  const looksLikeHtml = /<\s*[a-z][\s\S]*>/i.test(str);
  if (looksLikeHtml) {
    const sanitized = sanitizeRichHtml(str);
    const hasListMarkup = /<\s*(ol|ul|li)\b/i.test(sanitized);
    const readableText = getReadableHtmlText(sanitized);
    if (!hasListMarkup && hasInlineNumbering(readableText)) {
      return toMarkdownHtml(readableText);
    }
    return sanitized;
  }
  const looksLikeMarkdown =
    /(\*\*[^*]+\*\*)|(^\s*-\s+)|(^\s*(?:\d+|[٠-٩]+)\s*[.)-]\s*)|(\S\s+(?:\d+|[٠-٩]+)\s*[.)-]\s*)/m.test(str);
  if (looksLikeMarkdown) {
    return toMarkdownHtml(str);
  }
  return escapeHtml(str).replace(/\n/g, '<br/>');
};
